/**
 * Remembers privacy choices in this browser for the current guest or account.
 * Validates saved choices and preserves refusals when browser storage fails.
 */
import { createPrivacyChoices, trackingPurposes, type PrivacyChoices } from "@/types/privacyChoices"

export const privacyStorageKey = "landerz.privacyChoices"
const noticeVersion = 1
// A product default for this first pass, not a period prescribed by Law 25.
const choiceLifetimeMs = 180 * 24 * 60 * 60 * 1000
const declinedCookie = "landerz_privacy_declined"

function setDeclineOverride(declined: boolean): void {
	try {
		document.cookie = `${declinedCookie}=${declined ? "1" : ""}; Path=/; Max-Age=${declined ? choiceLifetimeMs / 1000 : 0}; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`
	} catch {
		/* Inaccessible storage remains denied in memory and through the refresh URL. */
	}
}

export function refreshWithPrivacyChoices(forceDecline: boolean): void {
	if (!forceDecline) {
		window.location.reload()
		return
	}
	const url = new URL(window.location.href)
	url.searchParams.set("privacy_choice", "declined")
	window.location.replace(url)
}

type PrivacyChoiceRecord = {
	version: number
	scope: string
	savedAt: number
	choices: PrivacyChoices
}

export function parsePrivacyChoices(raw: string | null, scope: string, now = Date.now()): PrivacyChoices | null {
	if (!raw) return null

	try {
		const record: PrivacyChoiceRecord = JSON.parse(raw)
		if (
			!record ||
			record.version !== noticeVersion ||
			record.scope !== scope ||
			!Number.isFinite(record.savedAt) ||
			record.savedAt > now ||
			now - record.savedAt >= choiceLifetimeMs ||
			!record.choices ||
			trackingPurposes.some(purpose => typeof record.choices[purpose] !== "boolean")
		)
			return null

		// Keep existing statistics/recording choices while discarding purposes that
		// are no longer supported. Removing a purpose must never grant another one.
		return Object.fromEntries(trackingPurposes.map(purpose => [purpose, record.choices[purpose]])) as PrivacyChoices
	} catch {
		return null
	}
}

export function readPrivacyChoices(scope: string): PrivacyChoices | null {
	try {
		if (
			new URL(window.location.href).searchParams.get("privacy_choice") === "declined" ||
			document.cookie.split(";").some(cookie => cookie.trim() === `${declinedCookie}=1`)
		)
			return createPrivacyChoices()
		return parsePrivacyChoices(localStorage.getItem(privacyStorageKey), scope)
	} catch {
		return null
	}
}

export function savePrivacyChoices(scope: string, choices: PrivacyChoices): boolean {
	try {
		const record: PrivacyChoiceRecord = { version: noticeVersion, scope, savedAt: Date.now(), choices }
		localStorage.setItem(privacyStorageKey, JSON.stringify(record))
		setDeclineOverride(false)
		const url = new URL(window.location.href)
		if (url.searchParams.has("privacy_choice")) {
			url.searchParams.delete("privacy_choice")
			window.history.replaceState(window.history.state, "", url)
		}
		return true
	} catch {
		setDeclineOverride(true)
		// Quota failures can leave an older grant behind. Remove it so a later
		// reload does not restore a choice that the visitor tried to withdraw.
		try {
			localStorage.removeItem(privacyStorageKey)
		} catch {
			/* Storage may be inaccessible entirely. */
		}
		return false
	}
}
